interface VADConfig {
  silenceThreshold: number;     // Amplitude threshold for silence detection (-60 dB)
  speechThreshold: number;      // Amplitude threshold for speech detection (-30 dB)
  minSpeechDuration: number;    // Minimum speech duration to trigger (ms)
  minSilenceDuration: number;   // Minimum silence duration to trigger (ms)
  bufferDuration: number;       // Audio buffer size for analysis (ms)
  smoothingFactor: number;      // Smoothing factor for energy calculation (0-1)
}

interface VADEvent {
  type: 'speech_start' | 'speech_end' | 'silence_start' | 'silence_end';
  timestamp: number;
  confidence: number;
  energyLevel: number;
}

interface VADStats {
  totalSpeechTime: number;
  totalSilenceTime: number;
  speechSegments: number;
  silenceSegments: number;
  averageEnergyLevel: number;
  speechRatio: number; // percentage of time spent speaking
}

class VoiceActivityDetector {
  private audioContext: AudioContext | null = null;
  private analyser: AnalyserNode | null = null;
  private sourceNode: MediaStreamAudioSourceNode | null = null;
  private isActive = false;
  private animationFrame: number | null = null;

  private config: VADConfig = {
    silenceThreshold: -60,      // dB
    speechThreshold: -30,       // dB
    minSpeechDuration: 500,     // ms
    minSilenceDuration: 1000,   // ms
    bufferDuration: 100,        // ms
    smoothingFactor: 0.8        // 0-1
  };

  private currentState: 'silence' | 'speech' = 'silence';
  private stateStartTime = 0;
  private smoothedEnergy = 0;
  private energyBuffer: number[] = [];
  private stats: VADStats = {
    totalSpeechTime: 0,
    totalSilenceTime: 0,
    speechSegments: 0,
    silenceSegments: 0,
    averageEnergyLevel: 0,
    speechRatio: 0
  };

  private onVADEventCallback?: (event: VADEvent) => void;
  private onStatsUpdateCallback?: (stats: VADStats) => void;

  /**
   * Initialize VAD with audio stream
   */
  async initialize(stream: MediaStream): Promise<boolean> {
    try {
      this.audioContext = new AudioContext();
      this.analyser = this.audioContext.createAnalyser();
      this.sourceNode = this.audioContext.createMediaStreamSource(stream);

      // Configure analyser
      this.analyser.fftSize = 256;
      this.analyser.smoothingTimeConstant = 0.3;

      // Connect audio nodes
      this.sourceNode.connect(this.analyser);

      this.reset();
      return true;

    } catch (error) {
      console.error('Failed to initialize VAD:', error);
      return false;
    }
  }

  /**
   * Start voice activity detection
   */
  start(callbacks: {
    onVADEvent?: (event: VADEvent) => void;
    onStatsUpdate?: (stats: VADStats) => void;
  } = {}): void {
    if (!this.analyser || this.isActive) return;

    this.onVADEventCallback = callbacks.onVADEvent;
    this.onStatsUpdateCallback = callbacks.onStatsUpdate;

    this.isActive = true;
    this.stateStartTime = Date.now();
    this.processAudio();

    console.log('🎤 Voice Activity Detection started');
  }

  /**
   * Stop voice activity detection
   */
  stop(): VADStats {
    this.isActive = false;

    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame);
      this.animationFrame = null;
    }

    console.log('Voice Activity Detection stopped');
    return { ...this.stats };
  }

  /**
   * Reset VAD statistics and state
   */
  reset(): void {
    this.currentState = 'silence';
    this.stateStartTime = Date.now();
    this.smoothedEnergy = 0;
    this.energyBuffer = [];
    this.stats = {
      totalSpeechTime: 0,
      totalSilenceTime: 0,
      speechSegments: 0,
      silenceSegments: 0,
      averageEnergyLevel: 0,
      speechRatio: 0
    };
  }

  /**
   * Update VAD configuration
   */
  updateConfig(newConfig: Partial<VADConfig>): void {
    this.config = { ...this.config, ...newConfig };
  }

  /**
   * Get current VAD configuration
   */
  getConfig(): VADConfig {
    return { ...this.config };
  }

  /**
   * Process audio data for voice activity detection
   */
  private processAudio(): void {
    if (!this.analyser || !this.isActive) return;

    const bufferLength = this.analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);
    this.analyser.getByteFrequencyData(dataArray);

    // Calculate RMS energy
    let sum = 0;
    for (let i = 0; i < bufferLength; i++) {
      sum += dataArray[i] * dataArray[i];
    }
    const rms = Math.sqrt(sum / bufferLength);

    // Convert to dB
    const energyDB = 20 * Math.log10(rms / 255);

    // Apply smoothing
    this.smoothedEnergy = this.config.smoothingFactor * this.smoothedEnergy +
                         (1 - this.config.smoothingFactor) * energyDB;

    // Add to energy buffer for averaging
    this.energyBuffer.push(this.smoothedEnergy);
    if (this.energyBuffer.length > 50) { // Keep last 50 readings
      this.energyBuffer.shift();
    }

    // Detect voice activity
    this.detectVoiceActivity(this.smoothedEnergy);

    // Update statistics
    this.updateStatistics();

    // Continue processing
    this.animationFrame = requestAnimationFrame(() => this.processAudio());
  }

  /**
   * Detect voice activity based on energy levels
   */
  private detectVoiceActivity(energyDB: number): void {
    const now = Date.now();
    const stateDuration = now - this.stateStartTime;

    if (this.currentState === 'silence') {
      // Check for speech start
      if (energyDB > this.config.speechThreshold) {
        if (stateDuration >= this.config.minSilenceDuration) {
          this.transitionToSpeech(now, energyDB);
        }
      }
    } else { // current state is 'speech'
      // Check for speech end
      if (energyDB < this.config.silenceThreshold) {
        if (stateDuration >= this.config.minSpeechDuration) {
          this.transitionToSilence(now, energyDB);
        }
      }
    }
  }

  /**
   * Transition from silence to speech
   */
  private transitionToSpeech(timestamp: number, energyLevel: number): void {
    // Update silence stats
    const silenceDuration = timestamp - this.stateStartTime;
    this.stats.totalSilenceTime += silenceDuration;
    this.stats.silenceSegments++;

    // Transition to speech
    this.currentState = 'speech';
    this.stateStartTime = timestamp;

    // Calculate confidence based on energy level
    const confidence = Math.min(1, Math.max(0,
      (energyLevel - this.config.silenceThreshold) /
      (this.config.speechThreshold - this.config.silenceThreshold)
    ));

    const event: VADEvent = {
      type: 'speech_start',
      timestamp,
      confidence,
      energyLevel
    };

    this.onVADEventCallback?.(event);
    console.log('🗣️ Speech detected');
  }

  /**
   * Transition from speech to silence
   */
  private transitionToSilence(timestamp: number, energyLevel: number): void {
    // Update speech stats
    const speechDuration = timestamp - this.stateStartTime;
    this.stats.totalSpeechTime += speechDuration;
    this.stats.speechSegments++;

    // Transition to silence
    this.currentState = 'silence';
    this.stateStartTime = timestamp;

    // Calculate confidence (inverse of energy level for silence)
    const confidence = Math.min(1, Math.max(0,
      (this.config.speechThreshold - energyLevel) /
      (this.config.speechThreshold - this.config.silenceThreshold)
    ));

    const event: VADEvent = {
      type: 'speech_end',
      timestamp,
      confidence,
      energyLevel
    };

    this.onVADEventCallback?.(event);
    console.log('🤫 Silence detected');
  }

  /**
   * Update running statistics
   */
  private updateStatistics(): void {
    if (this.energyBuffer.length > 0) {
      this.stats.averageEnergyLevel = this.energyBuffer.reduce((sum, val) => sum + val, 0) / this.energyBuffer.length;
    }

    const totalTime = this.stats.totalSpeechTime + this.stats.totalSilenceTime;
    if (totalTime > 0) {
      this.stats.speechRatio = (this.stats.totalSpeechTime / totalTime) * 100;
    }

    // Notify callback periodically (every 5 seconds)
    if (Date.now() % 5000 < 50) {
      this.onStatsUpdateCallback?.(this.stats);
    }
  }

  /**
   * Get current state and energy level
   */
  getCurrentState(): {
    state: 'silence' | 'speech';
    energyLevel: number;
    stateDuration: number;
    confidence: number;
  } {
    const stateDuration = Date.now() - this.stateStartTime;

    // Calculate confidence based on current energy and state
    let confidence = 0;
    if (this.currentState === 'speech') {
      confidence = Math.min(1, Math.max(0,
        (this.smoothedEnergy - this.config.silenceThreshold) /
        (this.config.speechThreshold - this.config.silenceThreshold)
      ));
    } else {
      confidence = Math.min(1, Math.max(0,
        (this.config.speechThreshold - this.smoothedEnergy) /
        (this.config.speechThreshold - this.config.silenceThreshold)
      ));
    }

    return {
      state: this.currentState,
      energyLevel: this.smoothedEnergy,
      stateDuration,
      confidence
    };
  }

  /**
   * Get detailed statistics
   */
  getDetailedStats(): VADStats & {
    averageSpeechDuration: number;
    averageSilenceDuration: number;
    currentState: 'silence' | 'speech';
    energyHistory: number[];
  } {
    const averageSpeechDuration = this.stats.speechSegments > 0
      ? this.stats.totalSpeechTime / this.stats.speechSegments
      : 0;

    const averageSilenceDuration = this.stats.silenceSegments > 0
      ? this.stats.totalSilenceTime / this.stats.silenceSegments
      : 0;

    return {
      ...this.stats,
      averageSpeechDuration,
      averageSilenceDuration,
      currentState: this.currentState,
      energyHistory: [...this.energyBuffer]
    };
  }

  /**
   * Check if currently detecting speech
   */
  isSpeechActive(): boolean {
    return this.currentState === 'speech';
  }

  /**
   * Get energy level as percentage (0-100)
   */
  getEnergyPercentage(): number {
    // Map from dB range to percentage
    const minDB = -80;
    const maxDB = 0;
    return Math.max(0, Math.min(100,
      ((this.smoothedEnergy - minDB) / (maxDB - minDB)) * 100
    ));
  }

  /**
   * Cleanup resources
   */
  cleanup(): void {
    this.stop();

    if (this.sourceNode) {
      this.sourceNode.disconnect();
      this.sourceNode = null;
    }

    if (this.analyser) {
      this.analyser.disconnect();
      this.analyser = null;
    }

    if (this.audioContext && this.audioContext.state !== 'closed') {
      this.audioContext.close();
      this.audioContext = null;
    }
  }

  /**
   * Export VAD events and statistics
   */
  exportData(): {
    config: VADConfig;
    stats: VADStats;
    energyHistory: number[];
    timestamp: Date;
  } {
    return {
      config: this.config,
      stats: this.stats,
      energyHistory: [...this.energyBuffer],
      timestamp: new Date()
    };
  }
}

export { VoiceActivityDetector };
export type { VADConfig, VADEvent, VADStats };